import { useId } from "react";
import { Link } from "react-router-dom";

import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from "lucide-react";

import { cn } from "@/lib/utils";

import { QUICK_MENU_TITLE } from "@/constants/quickMenu";
import type { QuickMenuItem } from "@/types/quickMenu";

interface QuickMenuProps {
  /** 보여줄 메뉴 칸. 지금 열려 있는 화면은 `isActive` 로 표시한다. */
  items: QuickMenuItem[];
  /** `설정하기` 를 눌렀을 때. 어떤 칸을 올릴지 고르는 다이얼로그를 여는 일은 페이지가 한다. */
  onOpenSettings: () => void;
  /** 칸 줄이 펼쳐져 있는지. `false` 면 제목 줄만 남는다. */
  isOpen: boolean;
  /** `접기` / `펼치기` 를 눌렀을 때. 접어 둔 상태를 기억하는 일은 훅이 한다. */
  onToggle: () => void;
}

const TEXT_BUTTON_CLASS =
  "flex items-center gap-1 rounded-sm text-xs text-white/85 outline-none hover:text-white focus-visible:ring-2 focus-visible:ring-white";

/**
 * 자주찾는 메뉴 띠 — 제목 줄 + 아이콘 칸 한 줄.
 *
 * `Layout` 이 헤더 바로 아래, 로그인 후 모든 화면 맨 위에 청회색 띠를 깔고, 칸은 종이색
 * (`--board-paper`), 지금 보고 있는 칸만 흰색에 테두리를 두른다. 띠는 화면 폭 전체로 깔리고, 내용(제목 줄 + 칸 줄)은 한
 * 덩어리로 **가운데**에 놓인다 — 제목은 첫 칸의 왼쪽 끝에 맞춘다. 칸이 한 줄에 다
 * 안 들어가는 좁은 화면에서는 줄바꿈된 칸도 가운데로 모인다. 칸의 제목·아이콘은 메뉴
 * 정의에서 오므로 이 컴포넌트는 그리기만 한다.
 *
 * "설정하기" 는 버튼일 뿐 다이얼로그를 직접 열지 않는다 — 열림 상태와 고른 목록은 훅
 * (`useQuickMenuSettings`)이 갖고, 페이지가 `QuickMenuSettings` 를 따로 그린다.
 *
 * 오른쪽 끝의 "접기" 는 칸 줄만 접는다 — 제목 줄(설정하기 · 펼치기)은 남는다. 접힌
 * 칸 줄은 `display: none` 이 아니라 **높이만 0** 으로 줄인다. 그래야 덩어리의 폭(= 칸
 * 줄의 폭)이 그대로라서, 접고 펼 때 가운데 놓인 제목이 옆으로 튀지 않는다. 대신 접힌
 * 칸에 Tab 이 들어가거나 스크린리더가 읽지 않도록 `inert` + `invisible` 을 같이 건다.
 */
const QuickMenu = ({
  items,
  onOpenSettings,
  isOpen,
  onToggle,
}: QuickMenuProps) => {
  const listId = useId();
  const ToggleIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

  return (
    <section aria-label={QUICK_MENU_TITLE} className="bg-board-band text-white">
      <div
        className={cn(
          "mx-auto w-fit max-w-[100rem] px-4 transition-[padding] duration-200 motion-reduce:transition-none sm:px-6 lg:px-10",
          isOpen ? "py-6" : "py-3",
        )}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold">{QUICK_MENU_TITLE}</h2>
          <button
            type="button"
            onClick={onOpenSettings}
            className={TEXT_BUTTON_CLASS}
          >
            <SettingsIcon className="size-3.5" aria-hidden />
            설정하기
          </button>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={listId}
            onClick={onToggle}
            className={cn(TEXT_BUTTON_CLASS, "ml-auto")}
          >
            <ToggleIcon className="size-3.5" aria-hidden />
            {isOpen ? "접기" : "펼치기"}
          </button>
        </div>

        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 motion-reduce:transition-none",
            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <ul
              id={listId}
              inert={!isOpen}
              className={cn(
                "flex flex-wrap justify-center gap-x-3 gap-y-4 pt-4",
                !isOpen && "invisible",
              )}
            >
              {items.map((item) => (
                <li key={item.url}>
                  <Link
                    to={item.url}
                    aria-current={item.isActive ? "page" : undefined}
                    className="group flex w-[4.5rem] flex-col items-center gap-2 outline-none"
                  >
                    <span
                      className={cn(
                        "flex size-14 items-center justify-center rounded-xl bg-board-paper text-board-ink transition-colors group-hover:bg-board-surface group-focus-visible:ring-2 group-focus-visible:ring-white",
                        item.isActive && "bg-board-surface ring-2 ring-sky-600",
                      )}
                    >
                      <item.icon className="size-5" aria-hidden />
                    </span>
                    <span className="text-center text-xs leading-snug break-keep">
                      {item.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export { QuickMenu };
